/**
 * Property Assignments Service
 * 
 * Handles assigning/unassigning properties to members and service providers
 * 
 * @module services/supabase/propertyAssignments
 */

import { supabase } from './client';
import { getHomeownerContext } from './homeownerMembers.service';
import { HomeOwnerObjectType } from './homeOwnerObjects.service';

export interface PropertyAssignment {
  id: string;
  property_id: string;
  member_user_id: string;
  primary_homeowner_id: string;
  assigned_at: string;
  assigned_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MemberWithAssignments {
  member_id: string;
  member_user_id: string;
  name: string;
  email: string;
  status: 'active' | 'pending';
  assigned_properties: string[]; // Array of property IDs
}

export interface PropertyWithAssignments {
  property_id: string;
  property_name: string;
  assigned_members: string[]; // Array of member user IDs
}

/**
 * #3556: what an assigned service provider may do on a house.
 * view_only = read; manage_devices = also edit devices and create its own door
 * codes; manage_alarms = also acknowledge/resolve alarms.
 */
export type ProviderPermissionLevel = 'view_only' | 'manage_devices' | 'manage_alarms';

export const PROVIDER_PERMISSION_LEVELS: readonly ProviderPermissionLevel[] = ['view_only', 'manage_devices', 'manage_alarms'];

/** Level change refused or failed; `code` is mapped to a translated message by the UI. */
export class ProviderPermissionError extends Error {
  constructor(public readonly code: 'not_owner' | 'failed', message: string) {
    super(message);
    this.name = 'ProviderPermissionError';
  }
}

export interface ServiceProviderSearchResult {
  user_id: string; // profile.id (ZentrianID)
  full_name: string | null;
  email: string;
  role: string;
  service_provider_id: number | null;
  service_provider_name: string | null;
  /** Level on the property, when loaded for a property's assignments */
  permissions?: ProviderPermissionLevel | null;
  /** Avatar path, returned by the name search (#3558) */
  avatar_storage_path?: string | null;
}

export const propertyAssignmentsService = {
  /**
   * Get all property assignments for the current primary homeowner
   */
  async getAssignments(): Promise<PropertyAssignment[]> {
    const { primaryHomeownerId } = await getHomeownerContext();

    const { data, error } = await supabase
      .from('homeowner_member_property_assignments')
      .select('*')
      .eq('primary_homeowner_id', primaryHomeownerId)
      .order('assigned_at', { ascending: false });

    if (error) {
      console.error('Error fetching property assignments:', error);
      throw new Error(`Failed to fetch property assignments: ${error.message}`);
    }

    return (data || []) as PropertyAssignment[];
  },

  /**
   * Get members with their assigned properties
   */
  async getMembersWithAssignments(): Promise<MemberWithAssignments[]> {
    const { primaryHomeownerId } = await getHomeownerContext();

    // Get all active members
    const { data: members, error: membersError } = await supabase
      .from('homeowner_members')
      .select(`
        id,
        member_user_id,
        status,
        member_profile:profiles!homeowner_members_member_user_id_fkey (
          id,
          email,
          full_name
        )
      `)
      .eq('primary_homeowner_id', primaryHomeownerId)
      .eq('status', 'active');

    if (membersError) {
      console.error('Error fetching members:', membersError);
      throw new Error(`Failed to fetch members: ${membersError.message}`);
    }

    // Get all assignments
    const assignments = await this.getAssignments();

    // Group assignments by member
    const assignmentsByMember = new Map<string, string[]>();
    assignments.forEach((assignment) => {
      const existing = assignmentsByMember.get(assignment.member_user_id) || [];
      existing.push(assignment.property_id);
      assignmentsByMember.set(assignment.member_user_id, existing);
    });

    // Combine members with their assignments
    return (members || []).map((member: any) => {
      const profile = member.member_profile?.[0] || member.member_profile;
      return {
        member_id: member.id,
        member_user_id: member.member_user_id,
        name: profile?.full_name || profile?.email || 'Unknown',
        email: profile?.email || '',
        status: member.status,
        assigned_properties: assignmentsByMember.get(member.member_user_id) || [],
      };
    }) as MemberWithAssignments[];
  },

  /**
   * Get properties with their assigned members
   */
  async getPropertiesWithAssignments(): Promise<PropertyWithAssignments[]> {
    const { primaryHomeownerId } = await getHomeownerContext();

    // Get all properties owned by primary homeowner
    const { data: properties, error: propertiesError } = await supabase
      .from('home_owner_objects')
      .select('id, name')
      .eq('owner_id', primaryHomeownerId)
      .order('name', { ascending: true });

    if (propertiesError) {
      console.error('Error fetching properties:', propertiesError);
      throw new Error(`Failed to fetch properties: ${propertiesError.message}`);
    }

    // Get all assignments for this primary homeowner
    const { data: assignments, error: assignmentsError } = await supabase
      .from('homeowner_member_property_assignments')
      .select('property_id, member_user_id')
      .eq('primary_homeowner_id', primaryHomeownerId);

    if (assignmentsError) {
      console.error('Error fetching assignments:', assignmentsError);
      throw new Error(`Failed to fetch assignments: ${assignmentsError.message}`);
    }

    // Group assignments by property
    const assignmentsByProperty = new Map<string, string[]>();
    (assignments || []).forEach((assignment) => {
      const existing = assignmentsByProperty.get(assignment.property_id) || [];
      existing.push(assignment.member_user_id);
      assignmentsByProperty.set(assignment.property_id, existing);
    });

    // Combine properties with their assignments
    return (properties || []).map((property) => ({
      property_id: property.id,
      property_name: property.name,
      assigned_members: assignmentsByProperty.get(property.id) || [],
    })) as PropertyWithAssignments[];
  },

  /**
   * Assign a property to a member
   */
  async assignPropertyToMember(propertyId: string, memberUserId: string): Promise<void> {
    const { primaryHomeownerId } = await getHomeownerContext();

    // Get current user for assigned_by
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('homeowner_member_property_assignments')
      .insert({
        property_id: propertyId,
        member_user_id: memberUserId,
        primary_homeowner_id: primaryHomeownerId,
        assigned_by: user.id,
      });

    if (error) {
      console.error('Error assigning property to member:', error);
      throw new Error(`Failed to assign property: ${error.message}`);
    }
  },

  /**
   * Unassign a property from a member
   */
  async unassignPropertyFromMember(propertyId: string, memberUserId: string): Promise<void> {
    const { primaryHomeownerId } = await getHomeownerContext();

    const { error } = await supabase
      .from('homeowner_member_property_assignments')
      .delete()
      .eq('property_id', propertyId)
      .eq('member_user_id', memberUserId)
      .eq('primary_homeowner_id', primaryHomeownerId);

    if (error) {
      console.error('Error unassigning property from member:', error);
      throw new Error(`Failed to unassign property: ${error.message}`);
    }
  },

  /**
   * Assign a property to a service provider
   * Uses the new service_provider_property_assignments table
   */
  async assignPropertyToServiceProvider(
    propertyId: string,
    serviceProviderId: number,
    permissions?: ProviderPermissionLevel
  ): Promise<void> {
    // Get current user ID (homeowner who is assigning the property)
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to assign properties');
    }

    // Use the new service_provider_property_assignments table
    const { error } = await supabase
      .from('service_provider_property_assignments')
      .insert({
        service_provider_id: serviceProviderId,
        property_id: propertyId,
        permissions: permissions || 'view_only',
        assigned_by: user.id,
      })
      .select()
      .single();

    if (error) {
      // If it's a unique constraint violation, the assignment already exists - that's okay
      if (error.code === '23505') {
        // Already assigned. Only change the level when one was chosen; never
        // fall back to view_only (that silently downgraded existing grants).
        if (permissions) {
          await this.updateServiceProviderPermission(propertyId, serviceProviderId, permissions);
        }
      } else {
        console.error('Error assigning property to service provider:', error);
        throw new Error(`Failed to assign property to service provider: ${error.message}`);
      }
    }
  },

  /**
   * #3556: change what an assigned provider may do on this property.
   * Only the property's owner can (RLS); 0 rows updated = not allowed.
   */
  async updateServiceProviderPermission(
    propertyId: string,
    serviceProviderId: number,
    permissions: ProviderPermissionLevel
  ): Promise<void> {
    const { data, error } = await supabase
      .from('service_provider_property_assignments')
      .update({
        permissions,
        updated_at: new Date().toISOString(),
      })
      .eq('service_provider_id', serviceProviderId)
      .eq('property_id', propertyId)
      .select('id');

    if (error) {
      console.error('Error updating provider permission:', error);
      throw new ProviderPermissionError('failed', `Failed to update permission: ${error.message}`);
    }
    if (!data || data.length === 0) {
      throw new ProviderPermissionError('not_owner', 'Only the property owner can change this permission');
    }
  },

  /**
   * Unassign a property from a service provider
   */
  async unassignPropertyFromServiceProvider(
    propertyId: string,
    serviceProviderId: number
  ): Promise<void> {
    // Get current user ID to ensure we only delete assignments made by this homeowner
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to unassign properties');
    }

    // Use the new service_provider_property_assignments table
    const { error } = await supabase
      .from('service_provider_property_assignments')
      .delete()
      .eq('service_provider_id', serviceProviderId)
      .eq('property_id', propertyId)
      .eq('assigned_by', user.id); // Only delete assignments made by current homeowner

    if (error) {
      console.error('Error unassigning property from service provider:', error);
      throw new Error(`Failed to unassign property from service provider: ${error.message}`);
    }
  },

  /**
   * Remove a service provider from one of the current user's properties (#3556).
   * Unlike unassignPropertyFromServiceProvider this does not depend on who made
   * the assignment; RLS ("Homeowners can delete assignments for their
   * properties") limits it to properties the user owns. The provider record
   * itself is never touched (only admins may change providers).
   */
  async removeServiceProviderFromProperty(propertyId: string, serviceProviderId: number): Promise<void> {
    const { data, error } = await supabase
      .from('service_provider_property_assignments')
      .delete()
      .eq('service_provider_id', serviceProviderId)
      .eq('property_id', propertyId)
      .select('id');

    if (error) {
      throw new Error(`Failed to remove service provider from property: ${error.message}`);
    }
    if (!data || data.length === 0) {
      throw new Error('Nothing removed: the provider is not assigned to this property, or you do not own it');
    }
  },

  /**
   * Get service providers with their assigned properties
   * Uses the new service_provider_property_assignments table
   */
  async getServiceProvidersWithAssignments(): Promise<
    Array<{
      service_provider_id: number;
      service_provider_name: string;
      assigned_properties: string[]; // Array of property IDs
      assigned_levels: Record<string, ProviderPermissionLevel>; // property ID -> level
    }>
  > {
    const { primaryHomeownerId } = await getHomeownerContext();

    // Get all properties owned by primary homeowner
    const { data: properties, error: propertiesError } = await supabase
      .from('home_owner_objects')
      .select('id')
      .eq('owner_id', primaryHomeownerId);

    if (propertiesError) {
      console.error('Error fetching properties:', propertiesError);
      throw new Error(`Failed to fetch properties: ${propertiesError.message}`);
    }

    const propertyIds = (properties || []).map((p) => p.id);

    if (propertyIds.length === 0) {
      return [];
    }

    // Get current user ID to filter assignments made by this homeowner
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Get all service provider property assignments for these properties (only those assigned by current homeowner)
    const { data: assignments, error: assignmentsError } = await supabase
      .from('service_provider_property_assignments')
      .select('service_provider_id, property_id, permissions')
      .in('property_id', propertyIds)
      .eq('assigned_by', user.id); // Only get assignments made by current homeowner

    if (assignmentsError) {
      console.error('Error fetching service provider property assignments:', assignmentsError);
      throw new Error(`Failed to fetch service provider property assignments: ${assignmentsError.message}`);
    }

    // Get unique service provider IDs
    const providerIds = [...new Set((assignments || []).map((a: any) => a.service_provider_id))];

    // Fetch service provider names separately
    let providersMap: Record<number, string> = {};
    if (providerIds.length > 0) {
      const { data: providers, error: providersError } = await supabase
        .from('service_providers')
        .select('id, name')
        .in('id', providerIds);

      if (providersError) {
        console.warn('Error fetching service provider names:', providersError);
      } else if (providers) {
        providersMap = providers.reduce((acc, provider) => {
          acc[provider.id] = provider.name;
          return acc;
        }, {} as Record<number, string>);
      }
    }

    // Group assignments by service provider
    const assignmentsByProvider = new Map<
      number,
      { name: string; properties: string[]; levels: Record<string, ProviderPermissionLevel> }
    >();

    (assignments || []).forEach((assignment: any) => {
      const providerId = assignment.service_provider_id;
      const existing = assignmentsByProvider.get(providerId) || {
        name: providersMap[providerId] || 'Unknown',
        properties: [],
        levels: {},
      };
      if (assignment.property_id) {
        existing.properties.push(assignment.property_id);
        existing.levels[assignment.property_id] = assignment.permissions || 'view_only';
      }
      assignmentsByProvider.set(providerId, existing);
    });

    // Convert to array
    return Array.from(assignmentsByProvider.entries()).map(([id, data]) => ({
      service_provider_id: id,
      service_provider_name: data.name,
      assigned_properties: data.properties,
      assigned_levels: data.levels,
    }));
  },

  /**
   * Get assigned properties for the current service provider
   * Returns properties from both:
   * 1. application_objects (properties owned by the service provider)
   * 2. home_owner_objects (properties assigned via service_provider_property_assignments)
   */
  async getAssignedPropertiesForServiceProvider(): Promise<HomeOwnerObjectType[]> {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Get current service provider ID using the database function
    const { data: spIdData, error: spIdError } = await supabase.rpc('get_current_service_provider_id');

    if (spIdError) {
      console.error('[getAssignedPropertiesForServiceProvider] Error fetching service provider ID:', spIdError);
      throw new Error(`Failed to fetch service provider ID: ${spIdError.message}`);
    }

    const serviceProviderId = spIdData;
    console.log('[getAssignedPropertiesForServiceProvider] Service Provider ID:', serviceProviderId);

    if (!serviceProviderId) {
      // User is not a service provider, return empty array
      console.log('[getAssignedPropertiesForServiceProvider] User is not a service provider');
      return [];
    }

    const allProperties: HomeOwnerObjectType[] = [];

    // 1. Get properties owned by the service provider (from application_objects)
    const { data: ownedProperties, error: ownedError } = await supabase
      .from('application_objects')
      .select('*')
      .eq('owner_id', user.id)
      .order('name', { ascending: true });

    if (ownedError) {
      console.error('[getAssignedPropertiesForServiceProvider] Error fetching owned properties:', ownedError);
      // Don't throw, continue to fetch assigned properties
    } else if (ownedProperties && ownedProperties.length > 0) {
      console.log('[getAssignedPropertiesForServiceProvider] Owned properties found:', ownedProperties.length);
      // Convert application_objects to HomeOwnerObjectType format
      ownedProperties.forEach((prop: any) => {
        allProperties.push({
          id: prop.id,
          name: prop.name,
          type: prop.type,
          location: prop.location,
          address: prop.address,
          description: prop.description,
          owner_id: prop.owner_id,
          created_at: prop.created_at,
          updated_at: prop.updated_at,
          status: prop.status,
        } as HomeOwnerObjectType);
      });
    }

    // 2. Get properties assigned via service_provider_property_assignments (new table)
    // Note: RLS policy will automatically filter to only show assignments for this service provider
    console.log('[getAssignedPropertiesForServiceProvider] Fetching assignments for SP ID:', serviceProviderId);
    const { data: assignments, error: assignmentsError } = await supabase
      .from('service_provider_property_assignments')
      .select('property_id');

    if (assignmentsError) {
      console.error('[getAssignedPropertiesForServiceProvider] Error fetching property assignments:', assignmentsError);
      // Don't throw, return what we have so far
    } else if (assignments && assignments.length > 0) {
      console.log('[getAssignedPropertiesForServiceProvider] Assignments fetched (RLS filtered):', assignments.length);

      // Extract property IDs
      const propertyIds = assignments.map((a: any) => a.property_id).filter((id: any) => id !== null);

      if (propertyIds.length > 0) {
        // Fetch the actual properties from home_owner_objects
        const { data: assignedProperties, error: propertiesError } = await supabase
          .from('home_owner_objects')
          .select('*')
          .in('id', propertyIds)
          .order('name', { ascending: true });

        if (propertiesError) {
          console.error('[getAssignedPropertiesForServiceProvider] Error fetching assigned properties:', propertiesError);
        } else if (assignedProperties && assignedProperties.length > 0) {
          console.log('[getAssignedPropertiesForServiceProvider] Assigned properties found:', assignedProperties.length);
          allProperties.push(...(assignedProperties as HomeOwnerObjectType[]));
        }
      }
    }

    // Note: We no longer use serviceproviderpermissions table - all assignments are in service_provider_property_assignments

    // Sort all properties by name
    allProperties.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    console.log('[getAssignedPropertiesForServiceProvider] Total properties found:', allProperties.length);
    return allProperties;
  },

  /**
   * Get assigned service providers for a specific property
   * Uses the new service_provider_property_assignments table
   */
  async getAssignedServiceProvidersForProperty(propertyId: string): Promise<ServiceProviderSearchResult[]> {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Get all service provider property assignments for this property
    const { data: assignments, error: assignmentsError } = await supabase
      .from('service_provider_property_assignments')
      .select('service_provider_id, permissions')
      .eq('property_id', propertyId);

    if (assignmentsError) {
      console.error('Error fetching property assignments:', assignmentsError);
      throw new Error(`Failed to fetch property assignments: ${assignmentsError.message}`);
    }

    if (!assignments || assignments.length === 0) {
      return [];
    }

    const serviceProviderIds = assignments.map((a: any) => a.service_provider_id);
    const levelByProvider = new Map<number, ProviderPermissionLevel | null>(
      assignments.map((a) => [a.service_provider_id, (a.permissions as ProviderPermissionLevel | null) ?? null])
    );

    // Get service provider details
    const { data: serviceProviders, error: spError } = await supabase
      .from('service_providers')
      .select('id, name, email')
      .in('id', serviceProviderIds);

    if (spError) {
      console.error('Error fetching service providers:', spError);
      throw new Error(`Failed to fetch service providers: ${spError.message}`);
    }

    if (!serviceProviders || serviceProviders.length === 0) {
      return [];
    }

    // Get profile details for each service provider
    const emails = serviceProviders.map((sp) => sp.email);
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, email, full_name, role')
      .in('email', emails)
      .eq('role', 'technician');

    if (profilesError) {
      console.error('Error fetching profiles:', profilesError);
    }

    const profilesMap = new Map((profiles || []).map((p) => [p.email, p]));

    return serviceProviders.map((sp) => {
      const profile = profilesMap.get(sp.email);
      return {
        user_id: profile?.id || '',
        full_name: profile?.full_name || null,
        email: sp.email,
        role: profile?.role || 'technician',
        service_provider_id: sp.id,
        service_provider_name: sp.name,
        permissions: levelByProvider.get(sp.id) ?? null,
      };
    });
  },

  /**
   * Search for service providers by ZentrianID (exact match on profile.id)
   */
  async searchServiceProviderByZentrianId(zentrianId: string): Promise<ServiceProviderSearchResult | null> {
    // Search for profile by ID with role technician and approved status
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id, email, full_name, role, is_approved')
      .eq('id', zentrianId)
      .eq('role', 'technician')
      .eq('is_approved', true)
      .maybeSingle();

    if (profileError) {
      console.error('Error searching profile by ZentrianID:', profileError);
      throw new Error(`Failed to search profile: ${profileError.message}`);
    }

    if (!profile) {
      return null;
    }

    // Get service provider record (match by email)
    let serviceProviderId: number | null = null;
    let serviceProviderName: string | null = null;

    const { data: serviceProvider, error: spError } = await supabase
      .from('service_providers')
      .select('id, name')
      .eq('email', profile.email)
      .eq('status', 'active')
      .maybeSingle();

    if (spError && spError.code !== 'PGRST116') {
      console.error('Error fetching service provider:', spError);
    }

    if (serviceProvider) {
      serviceProviderId = serviceProvider.id;
      serviceProviderName = serviceProvider.name;
    }

    return {
      user_id: profile.id,
      full_name: profile.full_name,
      email: profile.email,
      role: profile.role,
      service_provider_id: serviceProviderId,
      service_provider_name: serviceProviderName,
    };
  },

  /**
   * Search approved service providers by name (#3558: server-side search, so it
   * keeps working once other users' profiles are no longer readable directly)
   */
  async searchServiceProvidersByName(searchTerm: string): Promise<ServiceProviderSearchResult[]> {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return [];
    }

    const { data, error } = await supabase.rpc('search_service_providers', {
      p_query: searchTerm.trim(),
    });

    if (error) {
      console.error('Error searching service providers:', error);
      throw new Error(`Failed to search service providers: ${error.message}`);
    }

    return (data || []).map((row) => ({
      user_id: row.user_id,
      full_name: row.full_name,
      email: row.email,
      role: 'technician',
      service_provider_id: row.service_provider_id,
      service_provider_name: row.service_provider_name,
      avatar_storage_path: row.avatar_storage_path,
    }));
  },

  /**
   * Get service provider IDs assigned to a specific property
   * Returns array of service provider IDs that have permissions for the given property
   * Works for both homeowners and members - filters by primary homeowner's granted permissions
   */
  async getServiceProvidersForProperty(propertyId: string): Promise<number[]> {
    console.log('[getServiceProvidersForProperty] Property ID:', propertyId);

    // Simplified approach: Just get the service provider assignments for this property
    // Don't verify ownership - let RLS policies handle access control
    // Get all service provider property assignments for this property
    const { data: assignments, error: assignmentsError } = await supabase
      .from('service_provider_property_assignments')
      .select('service_provider_id, assigned_by')
      .eq('property_id', propertyId)
      .not('service_provider_id', 'is', null);

    if (assignmentsError) {
      console.error('[getServiceProvidersForProperty] Error fetching service provider assignments:', assignmentsError);
      throw new Error(`Failed to fetch service provider assignments: ${assignmentsError.message}`);
    }

    console.log('[getServiceProvidersForProperty] Assignments found:', assignments?.length || 0);
    if (assignments && assignments.length > 0) {
      console.log('[getServiceProvidersForProperty] Assignment details:', assignments.map((a: any) => ({
        service_provider_id: a.service_provider_id,
        assigned_by: a.assigned_by
      })));
    }

    if (!assignments || assignments.length === 0) {
      console.log('[getServiceProvidersForProperty] No assignments found for property');
      return [];
    }

    // Extract unique service provider IDs
    const serviceProviderIds = [...new Set(assignments.map((a: any) => a.service_provider_id))];
    console.log('[getServiceProvidersForProperty] Service provider IDs:', serviceProviderIds);
    return serviceProviderIds;
  },

  /**
   * Get properties assigned to the current member
   * Returns array of property IDs assigned to the current member user
   */
  async getAssignedPropertiesForMember(): Promise<string[]> {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Query homeowner_member_property_assignments for this member
    const { data: assignments, error: assignmentsError } = await supabase
      .from('homeowner_member_property_assignments')
      .select('property_id')
      .eq('member_user_id', user.id);

    if (assignmentsError) {
      console.error('Error fetching member property assignments:', assignmentsError);
      throw new Error(`Failed to fetch member property assignments: ${assignmentsError.message}`);
    }

    if (!assignments || assignments.length === 0) {
      return [];
    }

    // Extract property IDs
    return assignments.map((assignment: any) => assignment.property_id);
  },
};

